import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabaseAdmin as supabase } from "@/integrations/supabase/admin-client";
import { clearStoredCrmSession, getStoredCrmSession } from "@/lib/crmAuth";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkRole = async (uid: string | undefined) => {
    if (!uid) {
      setIsAdmin(false);
      return false;
    }
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
      return !!data;
    } catch {
      const stored = getStoredCrmSession();
      const canUseStoredAdmin = stored?.user.id === uid;
      setIsAdmin(!!canUseStoredAdmin);
      return !!canUseStoredAdmin;
    }
  };

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setTimeout(() => checkRole(s?.user?.id), 0);
    });

    supabase.auth.getSession().then(({ data }) => {
      const stored = getStoredCrmSession();
      const activeSession = data.session ?? stored?.session ?? null;
      const activeUser = activeSession?.user ?? stored?.user ?? null;

      setSession(activeSession);
      setUser(activeUser);

      if (!data.session && stored?.user) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      checkRole(activeUser?.id).finally(() => setLoading(false));
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    clearStoredCrmSession();
    setSession(null);
    setUser(null);
    setIsAdmin(false);
  };

  const refreshRole = async () => {
    await checkRole(user?.id);
  };

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, loading, signOut, refreshRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}